import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarDaysIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const ServiceCard = ({ service, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
    >
      {/* Service Header */}
      <div className={`${service.color} p-6 text-white`}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-4xl">{service.icon}</div>
          <div className="text-right">
            <div className="text-2xl font-bold">{service.pricing.hourly}</div>
            <div className="text-sm opacity-90">per hour</div>
          </div>
        </div>
        <h3 className="text-xl font-bold font-display mb-2">{service.title}</h3>
        <p className="text-sm opacity-90">{service.description}</p>
        <div className="mt-3">
          <span className="inline-block bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
            {service.category}
          </span>
        </div>
      </div>

      {/* Service Details */}
      <div className="p-6">
        {/* Pricing Packages */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Pricing Packages:
          </h4>
          <div className="space-y-2">
            {service.pricing.packages.map((pkg, idx) => (
              <div key={idx} className="flex justify-between items-center bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">{pkg.name}</span>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{pkg.price}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">{pkg.duration}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technologies */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Technologies:
          </h4>
          <div className="flex flex-wrap gap-2">
            {service.technologies.slice(0, 6).map((tech, idx) => (
              <span key={idx} className="text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-cyan-200 px-2 py-1 rounded">
                {tech}
              </span>
            ))}
            {service.technologies.length > 6 && (
              <span className="text-xs text-gray-500 dark:text-gray-500">
                +{service.technologies.length - 6} more
              </span>
            )}
          </div>
        </div>

        {/* Expandable Details */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 mb-4"
        >
          <span className="text-sm font-medium">View Details</span>
          {isExpanded ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Features */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                What's Included:
              </h4>
              <ul className="space-y-1">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Deliverables */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Deliverables:
              </h4>
              <ul className="space-y-1">
                {service.deliverables.map((deliverable, idx) => (
                  <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <DocumentTextIcon className="h-4 w-4 text-indigo-500 mr-2 flex-shrink-0" />
                    {deliverable}
                  </li>
                ))}
              </ul>
            </div>

            {/* Suitable For */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Perfect For:
              </h4>
              <div className="flex flex-wrap gap-2">
                {service.suitableFor.map((audience, idx) => (
                  <span key={idx} className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                    {audience}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2 mt-6">
          <button className="w-full bg-gradient-to-r from-indigo-600 to-cyan-500 text-white py-2.5 px-4 rounded-lg text-sm font-semibold hover:from-indigo-700 hover:to-cyan-600 transition-all duration-200 transform hover:scale-[1.02] flex items-center justify-center">
            <CalendarDaysIcon className="h-4 w-4 mr-2" />
            Book Consultation
          </button>
        
        </div>
      </div>
    </motion.div>
  );
};

export default ServiceCard;
